import {ApolloLink, Operation, FetchResult, Observable} from "@apollo/client";
import {GraphQLError, print} from "graphql";
import {Client, ClientOptions, createClient} from "graphql-ws";
import {UserManager} from "oidc-client-ts";

export class WebSocketLink extends ApolloLink {
    private client: Client;
    private userManager?: UserManager;

    constructor(options: ClientOptions, userManager?: UserManager) {
        super();
        this.client = createClient(options);
        this.userManager = userManager;
    }

    public override request(operation: Operation): Observable<FetchResult> {
        return new Observable((sink) => {
            // Get access token from UserManager if available
            const getToken = async (): Promise<string | null> => {
                if (!this.userManager) return null;
                try {
                    const user = await this.userManager.getUser();
                    return user?.access_token || null;
                } catch (error) {
                    console.warn("Failed to get access token for WebSocket:", error);
                    return null;
                }
            };

            getToken()
                .then((token) => {
                    if (token) {
                        operation.extensions.authorization = `Bearer ${token}`;
                    }

                    return this.client.subscribe<FetchResult>(
                        {...operation, query: print(operation.query)},
                        {
                            next: sink.next.bind(sink),
                            complete: sink.complete.bind(sink),
                            error: (err) => {
                                if (err instanceof Error) {
                                    return sink.error(err);
                                }

                                if (err instanceof CloseEvent) {
                                    return sink.error(
                                        // reason will be available on clean closes
                                        new Error(`Socket closed with event ${err.code} ${err.reason || ""}`),
                                    );
                                }

                                return sink.error(
                                    new Error((err as GraphQLError[]).map(({message}) => message).join(", ")),
                                );
                            },
                        },
                    );
                })
                .catch((error) => {
                    sink.error(new Error(`Failed to initialize WebSocket connection: ${error.message}`));
                });
        });
    }
}
